import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Tag as TagIcon, Trash2, X, Loader2, RefreshCw, Edit2 } from 'lucide-react';
import { crmTagsApi, CrmTag } from '../services/crmApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
export function Tags() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [tags, setTags] = useState<CrmTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTag, setEditingTag] = useState<CrmTag | null>(null);
  const [formName, setFormName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const team = currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const fetchTags = async () => {
    setLoading(true);
    try {
      const data = await crmTagsApi.list();
      setTags(data);
    } catch (err) {
      console.error('Failed to fetch tags:', err);
      toast.error('Failed to load tags');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchTags();
  }, []);
  // Filter by team and region scope
  const filteredTags = tags.filter(t => {
    // Team filter: only show tags belonging to current workspace or with no team
    if (t.Team && t.Team !== currentWorkspace.name && t.Team !== team) return false;
    // Region filter
    if (currentSubRegion) {
      return !t.Region || t.Region === currentSubRegion;
    }
    return true;
  });
  const handleOpenCreate = () => {
    setEditingTag(null);
    setFormName('');
    setShowModal(true);
  };
  const handleOpenEdit = (tag: CrmTag) => {
    setEditingTag(tag);
    setFormName(tag.Name || '');
    setShowModal(true);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) return;
    setIsSubmitting(true);
    try {
      const payload: {
        Name: string;
        Team: string;
        Region?: string;
      } = {
        Name: formName.trim(),
        Team: team
      };
      if (currentSubRegion) {
        payload.Region = currentSubRegion;
      }
      if (editingTag) {
        await crmTagsApi.update(editingTag.id, payload);
        toast.success('Tag updated successfully');
      } else {
        await crmTagsApi.create(payload);
        toast.success('Tag created successfully');
      }
      setShowModal(false);
      fetchTags();
    } catch (err) {
      console.error('Failed to save tag:', err);
      toast.error(editingTag ? 'Failed to update tag' : 'Failed to create tag');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      await crmTagsApi.delete(id);
      toast.success('Tag deleted successfully');
      setDeleteConfirm(null);
      fetchTags();
    } catch (err) {
      console.error('Failed to delete tag:', err);
      toast.error('Failed to delete tag');
    } finally {
      setIsDeleting(false);
    }
  };
  return <div className="space-y-6" data-id="element-4843">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-4844">
        <div className="min-w-0" data-id="element-4845">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4846">Tags</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-4847">
            Manage tags for segmenting contacts (synced with Zoho CRM).
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-4848">
          <button onClick={fetchTags} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" title="Refresh" data-id="element-4849">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-4850" />
          </button>
          <button onClick={handleOpenCreate} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap flex-shrink-0" data-id="element-4851">
            <Plus className="h-4 w-4 flex-shrink-0" data-id="element-4852" />
            <span data-id="element-4853">Create Tag</span>
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-4854">
        {loading && filteredTags.length === 0 ? <div className="p-12 text-center" data-id="element-4855">
            <Loader2 className="mx-auto h-8 w-8 text-blue-500 animate-spin" data-id="element-4856" />
            <p className="mt-2 text-sm text-gray-500" data-id="element-4857">
              Loading tags from CRM...
            </p>
          </div> : filteredTags.length === 0 ? <div className="p-12 text-center" data-id="element-4858">
            <TagIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-4859" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-4860">
              No tags found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-4861">
              {currentSubRegion ? `No tags found for region "${currentSubRegion}".` : 'Create tags to segment your contacts in Zoho CRM.'}
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-4862">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-4863">
              <thead className="bg-gray-50" data-id="element-4864">
                <tr data-id="element-4865">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4866">
                    Tag Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4867">
                    Team
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4868">
                    Region
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4869">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-4870">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-4871">
                {filteredTags.map(tag => <tr key={tag.id} className="hover:bg-gray-50" data-id="element-4872">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-4873">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200" data-id="element-4874">
                        <TagIcon className="h-3.5 w-3.5 flex-shrink-0" data-id="element-4875" />
                        {tag.Name}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4876">
                      {tag.Team || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4877">
                      {tag.Region || 'All Regions'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-4878">
                      {tag.Created_Time ? new Date(tag.Created_Time).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-4879">
                      <div className="flex items-center justify-end gap-2" data-id="element-4880">
                        <button onClick={() => handleOpenEdit(tag)} className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded" title="Edit" data-id="element-4881">
                          <Edit2 className="h-4 w-4" data-id="element-4882" />
                        </button>
                        <button onClick={() => setDeleteConfirm(tag.id)} className="text-red-600 hover:text-red-900 p-2 hover:bg-red-50 rounded" title="Delete" data-id="element-4883">
                          <Trash2 className="h-4 w-4" data-id="element-4884" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>

      {/* Create/Edit Modal */}
      {showModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-4885">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-4886">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)} data-id="element-4887" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-4888">
              <div className="flex items-center justify-between mb-4" data-id="element-4889">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-4890">
                  {editingTag ? 'Edit Tag' : 'Create New Tag'}
                </h3>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-500" data-id="element-4891">
                  <X className="h-5 w-5" data-id="element-4892" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-4893">
                <div data-id="element-4894">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4895">
                    Tag Name
                  </label>
                  <input type="text" required className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formName} onChange={e => setFormName(e.target.value)} placeholder="e.g., VIP, Lead, Customer" data-id="element-4896" />
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-4897">
                  <div data-id="element-4898">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4899">
                      Team
                    </label>
                    <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team} disabled data-id="element-4900" />
                  </div>
                  <div data-id="element-4901">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4902">
                      Region
                    </label>
                    <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={currentSubRegion || 'All Regions'} disabled data-id="element-4903" />
                  </div>
                </div>
                <p className="text-xs text-gray-400" data-id="element-4904">
                  Team and Region are set from your current scope
                </p>
                <div className="flex justify-end gap-3 pt-4" data-id="element-4905">
                  <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4906">
                    Cancel
                  </button>
                  <button type="submit" disabled={isSubmitting || !formName.trim()} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2" data-id="element-4907">
                    {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-4908" />}
                    {editingTag ? 'Update Tag' : 'Create Tag'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>}

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-4909">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-4910">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setDeleteConfirm(null)} data-id="element-4911" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-4912">
              <div className="flex items-center gap-4 mb-4" data-id="element-4913">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-4914">
                  <Trash2 className="h-5 w-5 text-red-600" data-id="element-4915" />
                </div>
                <div data-id="element-4916">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-4917">
                    Delete Tag
                  </h3>
                  <p className="text-sm text-gray-500" data-id="element-4918">
                    Are you sure? This will remove the tag from CRM.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3" data-id="element-4919">
                <button onClick={() => setDeleteConfirm(null)} disabled={isDeleting} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-4920">
                  Cancel
                </button>
                <button onClick={() => handleDelete(deleteConfirm)} disabled={isDeleting} className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-id="element-4921">
                  {isDeleting && <Loader2 className="h-4 w-4 animate-spin" data-id="element-4922" />}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}